import { Component, EventEmitter, inject, OnInit, OnDestroy, Output, ViewChild, WritableSignal, signal } from '@angular/core';
import { AuthService, DriverInformationService } from '../../../../_shared/services';
import { FormsModule, NgForm, NgModel, ReactiveFormsModule } from '@angular/forms';
import { DriverInformationDetailed } from '../../models/driver-detailed-information.model';
import { DriverTripOverview } from '../../models/driver-trip-overview.model';
import { DriverInformationListService } from '../../services/driver-information-list.service';
import { ToastrService } from 'ngx-toastr';
import { defaultProfile } from '../../../../_shared/helpers/get-user-profile.helper';
import { CommonModule, DatePipe } from '@angular/common';
import { NgbModal, NgbModalOptions, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { DriverInformationSettingsComponent } from '../modals/driver-information-settings-modal/driver-information-settings-modal';
import { DriverInformationSettings } from '../../../../_shared/models/drivers/management/driver-information-settings.model';
import { AvatarComponent } from "../../../../_shared/components/avatar/avatar.component";
import { differenceInDays, endOfDay, parseISO } from 'date-fns';
import { FormErrorMessageComponent } from "../../../../_shared/components/form-error-message/form-error-message.component";
import { isExpired, isExpiringSoon } from '../../../../_shared/helpers/license-status.helper';
import { ConfirmationOptions } from '../../../../_shared/models/modals/confirmation-options.model';
import { ConfirmationModalService } from '../../../../_shared/services/modals/confirmation-modal.service';
import { Subject, Subscription } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { AssignmentFilterService } from '../../../driver-vehicle-assignment/services/assignment-filter.service';
import { User } from '../../../../_shared/models/user.model';
import { AccountService } from '../../../../_shared/services';
import { buildAssetTree } from '../../../../_shared/helpers/hierarchy.helper';
import { UnifiedTreeItem } from '../../../../_shared/models/driver.model';
import { SingleAssignmentModalConfig } from '../../../../_shared/constants/assignment-modal-config.constants';
import { ModalConfig } from '../../../driver-vehicle-assignment/models/vehicle-assignment.model';
import { DriverAssignmentFilter } from '../../../../_shared/models';
import { VehicleAssignmentModalComponent } from '../../../_shared/components/modals/driver-assignment/vehicle-assignment-modal.component';
import { FlatpickrDatePickerComponent } from '../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { UserRole } from '../../../../_shared/models/user-roles.enum';
import { DriverUserCredentialModalComponent } from '../modals/driver-user-credential-modal/driver-user-credential-modal.component';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-driver-information-details',
  standalone: true,
  providers: [],
  imports: [DatePipe, FormsModule, ReactiveFormsModule, AvatarComponent, NgbTooltip, FormErrorMessageComponent, CommonModule, RouterLink, FlatpickrDatePickerComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './driver-information-details.component.html',
  styleUrl: './driver-information-details.component.scss'
})
export class DriverInformationDetailsComponent implements OnInit, OnDestroy {
  @Output() loaded = new EventEmitter<boolean>();
  @ViewChild('driverInformationForm') driverInformationForm!: NgForm;

  user: User;
  hierarchicalAssetsTree: UnifiedTreeItem[] = [];
  assetsLoaded: WritableSignal<boolean> = signal(false);
  driver: DriverInformationDetailed | null = null
  driverTripsOverview: DriverTripOverview | null = null;
  isLoading = false;
  isAssetFilterLoading = false;
  isUpdating = false;
  formSubmitted = false;
  photoUrl: string | null = null;
  photoFile: string | ArrayBuffer | null = null;
  selectedPhotoFile?: File;
  defaultProfile = defaultProfile;
  public isDriverSelected: boolean = false;
  public driverSettings: DriverInformationSettings | null;
  public isPayrollAdmin: boolean;
  public hasFleetManagerRole: boolean;
  public driverNotificationMessage: string = '';
  public driverNotificationCount: number = 0;
  public licenseSoonExpired: boolean = false;
  public licenseExpired: boolean = false;
  public licenseNotificationMessage: string = '';
  public pdpHasExpired: boolean = false;
  public pdpNotificationMessage: string = '';
  public pdpSoonExpired: boolean = false;
  public alertMessage: string;
  public oldIdNumber: string;
  public passportAlertMessage: string;
  public oldPassportNumber: string;
  public licenseHasExpired: boolean = false;
  public assignedVehiclesCount: number = 0;

  private readonly cancelSubject = new Subject<void>();
  private driverSubscription: Subscription;
  private driverListService = inject(DriverInformationListService);
  private driverInformationService = inject(DriverInformationService);
  private accountService = inject(AccountService)
  private confirmationModalService = inject(ConfirmationModalService);
  private toastr = inject(ToastrService);
  private modalService = inject(NgbModal);
  private authService = inject(AuthService);
  private errorHandlerService = inject(ErrorHandlerService);
  private router = inject(Router);
  private assignmentFilterService = inject(AssignmentFilterService);
  private transloco = inject(TranslocoService);

  ngOnInit(): void {
    this.isLoading = true;
    this.photoUrl = this.defaultProfile;
    this.user = this.authService.getUser();
    this.isPayrollAdmin = this.authService.hasRole(UserRole.DriverPayrollAdmin);
    this.hasFleetManagerRole = this.authService.hasRole(UserRole.GTFleetManager);
    this.driverSubscription = this.driverListService.selectedDriver$.subscribe(driver => {
      this.clearSelectedPhoto();

      if (driver?.id) {
        this.isDriverSelected = true;
        this.photoUrl = driver.image || defaultProfile;
        this.loadDriverDetails(driver.id);
      } else {
        this.isDriverSelected = false;
        this.driver = null;
      }
    });
  }

  ngOnDestroy(): void {
    this.driverSubscription.unsubscribe();

    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  getErrorMessage(control: NgModel, _label: string): string | null {
    if (!control || !control.errors) {
      return null
    }

    if (control.errors['required']) {
      return this.transloco.translate('driverInformation.validation.required');
    }

    if (control.errors['pattern']) {
      return this.transloco.translate('driverInformation.validation.digitsOnly');
    }

    if (control.errors['minlength'] || control.errors['maxlength']) {
      return this.transloco.translate('driverInformation.validation.phoneLength');
    }

    return null;
  }

  get appCredentialLabel(): string {
    return this.transloco.translate(
      this.driver?.hasLoginAccount ? 'driverInformation.credentials.manage' : 'driverInformation.credentials.generate'
    );
  }

  isDriverIdRequired(): boolean {
    return !this.driver?.passportNumber && !this.driver?.driverIdNumber;
  }

  isPassportRequired(): boolean {
    return !this.driver?.driverIdNumber && !this.driver?.passportNumber;
  }

  validateFields(): boolean {
    return !!this.driver?.driverIdNumber || !!this.driver?.passportNumber;
  }

  isPdpExpired(): boolean | null {
    const pdpDate = this.driver?.professionalDriversPermitRenewalDate;

    if (!pdpDate || pdpDate === '0001-01-01') {
      return null;
    }

    this.pdpHasExpired = isExpired(pdpDate)!;

    if (this.pdpHasExpired) {
      this.pdpNotificationMessage = this.transloco.translate('driverInformation.status.documentExpired');
    }

    return this.pdpHasExpired;
  }

  isLicenseExpiringSoon(): boolean | null {
    const licenseDate = this.driver?.driverLicenseRenewal;

    if (!licenseDate || licenseDate === '0001-01-01') {
      return null;
    }

    const expiringSoon = isExpiringSoon(licenseDate);

    if (expiringSoon === null) {
      return null;
    }

    if (expiringSoon) {
      const renewalDate = parseISO(licenseDate);
      const today = endOfDay(new Date());
      const daysUntilExpiration = differenceInDays(endOfDay(renewalDate), today);

      this.driverNotificationMessage = daysUntilExpiration === 0
        ? this.transloco.translate('driverInformation.status.expiringToday')
        : this.transloco.translate('driverInformation.status.expiringInDays', { days: daysUntilExpiration });

      return true;
    }

    return false;
  }

  isLicenseExpired(): boolean | null {
    const licenseDate = this.driver?.driverLicenseRenewal;
    if (!licenseDate) {
      return null;
    }

    const expired = isExpired(licenseDate);
    if (expired === null) {
      return null;
    }

    this.licenseHasExpired = expired;
    if (this.licenseHasExpired) {
      this.driverNotificationMessage = this.transloco.translate('driverInformation.status.documentExpired');
    }

    return this.licenseHasExpired;
  }

  isPdpExpiringSoon(): boolean {
    const pdpDate = this.driver?.professionalDriversPermitRenewalDate;

    if (!pdpDate) {
      return false;
    }

    const expiringSoon = isExpiringSoon(pdpDate);

    if (!expiringSoon) {
      return false;
    }

    const renewalDate = parseISO(pdpDate);
    const today = endOfDay(new Date());
    const daysUntilExpiration = differenceInDays(endOfDay(renewalDate), today);

    this.pdpNotificationMessage = daysUntilExpiration === 0
      ? this.transloco.translate('driverInformation.status.expiringToday')
      : this.transloco.translate('driverInformation.status.expiringInDays', { days: daysUntilExpiration });

    return true;
  }

  isFormValid(): boolean {
    return !!this.driver?.lastName;
  }

  async loadDriverDetails(driverId: number): Promise<void> {
    try {
      this.isLoading = true;

      this.cancelSubject.next();
      await Promise.all([
        this.fetchDriverInformation(driverId),
        this.fetchDriverTripsOverview(driverId),
        this.fetchAssignedVehiclesCount(driverId)
      ]);

      this.isLoading = false;
      this.formSubmitted = false;
      this.loaded.emit(true);
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isLoading = false;
          this.formSubmitted = false;
          this.loaded.emit(true);
        }
      });
    }
  }

  async fetchAssignedVehiclesCount(driverId: number): Promise<void> {
    const response = await this.driverInformationService.getAssignedVehiclesCount(driverId, this.cancelSubject);
    this.assignedVehiclesCount = response?.data?.count ?? 0;
  }

  async fetchDriverInformation(id: number): Promise<void> {
    const response = await this.driverInformationService.getDriverInformationDetail(id, this.cancelSubject);
    if (response?.data) {
      this.driver = {
        ...response.data,
        dateOfBirth: response.data.dateOfBirth?.split('T')[0] ?? '',
        driverLicenseRenewal: response.data.driverLicenseRenewal?.split('T')[0] ?? '',
        professionalDriversPermitRenewalDate: response.data.professionalDriversPermitRenewalDate?.split('T')[0] ?? ''
      };

      this.validateRenewalDates();
      this.setDriverNotifications();
    }
  }

  async fetchDriverTripsOverview(driverId: number): Promise<void> {
    const response = await this.driverInformationService.getDriverTripOverview(driverId, this.cancelSubject);
    this.driverTripsOverview = response?.data ?? null;
  }

  async updateDriverDetails(): Promise<void> {
    this.formSubmitted = true;

    if (!this.driverInformationForm?.valid) {
      this.toastr.warning(this.transloco.translate('driverInformation.toasts.completeRequiredFields'));
      return;
    }

    this.oldIdNumber = this.driver?.driverIdNumber!;
    this.oldPassportNumber = this.driver?.passportNumber!;
    this.isUpdating = true;
    this.alertMessage = '';
    this.passportAlertMessage = '';
    try {
      await this.driverInformationService.updateDriverInformationDetail(this.getFormData(this.driver!));

      this.toastr.success(this.transloco.translate('driverInformation.toasts.driverUpdated'));
      this.clearSelectedPhoto();
      this.driverListService.refreshDriverList();
      this.formSubmitted = false;
      this.isUpdating = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: (message: string, code: string) => {
          this.isUpdating = false;
          if (code === "driver-information/driver-id-exists") {
            this.alertMessage = message;
            return;
          }

          if (code === "driver-information/driver-passport-exists") {
            this.passportAlertMessage = message;
            return;
          }
        }
      });
    }
  }

  async getDriverSettingsAsync(id: number): Promise<void> {
    try {
      this.cancelSubject.next();
      const response = await this.driverInformationService.getDriverSettings(id, this.cancelSubject);
      if (response?.data) {
        this.driverSettings = response?.data;
      }
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.driverSettings = null;
        }
      });
    }
  }

  async deleteDriver(driverId: number): Promise<void> {
    try {
      this.loaded.emit(false);
      await this.driverInformationService.deleteDriver(driverId);

      this.toastr.success(this.transloco.translate('driverInformation.toasts.driverRemoved'));
      this.toastr.success(this.transloco.translate('driverInformation.toasts.assetsUnassigned'));

      this.driverListService.refreshDriverList();
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
    }
  }

  async getUserAssetsAsync() {
    if (!!this.user) {
      this.isAssetFilterLoading = true;
      try {
        this.cancelSubject.next();
        const response = await this.accountService.getUserAccountAssets([], this.cancelSubject);
        if (response.data) {
          this.accountService.setAssets(response.data);
          this.hierarchicalAssetsTree = buildAssetTree(response.data);
        }

        this.isAssetFilterLoading = false;
        this.assetsLoaded.set(true);
      } catch (error: any) {
        this.errorHandlerService.handleError(error, {
          showToast: true,
          callback: () => {
            this.isAssetFilterLoading = false;
            this.assetsLoaded.set(true);
          }
        });
      }
    }
  }

  async onAssignAssetsClick(driverId: number) {
    await this.getUserAssetsAsync();
    this.openSingleAssignmentModal(driverId);
  }

  openSingleAssignmentModal(driverId: number): void {
    const config = SingleAssignmentModalConfig;
    const modalConfig: ModalConfig = {
      ...config,
      title: this.transloco.translate('vehicleAssignment.modals.assignDriver.title'),
      confirmationButtonText: this.transloco.translate('common.assign'),
      cancelButtonText: this.transloco.translate('common.cancel'),
      hierarchicalAssetsTree: this.hierarchicalAssetsTree,
      assignmentFilter: {
        drivers: [{
          id: driverId
        }]
      } as DriverAssignmentFilter,
      assetsLoaded: this.assetsLoaded,
    };

    this.assignmentFilterService.setReadOnlyBillingAccount(this.driver!.billingAccountId);
    const modalOptions: NgbModalOptions = { centered: true, backdrop: 'static', size: 'xl' };
    const modalRef = this.modalService.open(VehicleAssignmentModalComponent, modalOptions);
    modalRef.componentInstance.config = modalConfig;
  }

  showAssignedVehicles() {
    const driverId = this.driver!.id;
    const fullName = `${this.driver!.firstName} ${this.driver!.lastName}`;
    const hasFleetManagerRole = this.hasFleetManagerRole;
    const isDriverDeleted = false;

    this.assignmentFilterService.selectedFilterType.set('assigned');
    this.router.navigate(['/driver-management/driver-vehicle-assignment-list'], { state: { fullName, driverId, hasFleetManagerRole, isDriverDeleted } });
  }

  onPhotoSelected(event: Event): void {
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files?.[0];

    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        this.photoFile = reader.result;
        this.photoUrl = reader.result as string;
      };
      reader.readAsDataURL(file);
      this.selectedPhotoFile = file;
    }
  }

  openDriverInformationSettingsModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl'
    };

    const modalRef = this.modalService.open(DriverInformationSettingsComponent, modalOptions);
    modalRef.componentInstance.driverId = this.driver?.id;
  }

  openDeleteDriverConfirmationModal(driver: DriverInformationDetailed) {
    const options: ConfirmationOptions = {
      title: this.transloco.translate('driverInformation.removePrompt.title'),
      message: this.transloco.translate('driverInformation.removePrompt.message', { name: `${driver.firstName} ${driver.lastName}` }),
      warningMessage: this.transloco.translate('driverInformation.removePrompt.warning'),
      confirmText: this.transloco.translate('driverInformation.removePrompt.confirm'),
      declineText: this.transloco.translate('driverInformation.removePrompt.decline'),
    }

    this.confirmationModalService.showConfirmationModal(options).then(async () => {
      await this.deleteDriver(driver.id);
    });
  }

  openAppCredentialsButton(): void {
    const modalRef = this.modalService.open(DriverUserCredentialModalComponent, {
      centered: true,
      backdrop: 'static',
      size: 'md',
    });
    modalRef.componentInstance.driverId = this.driver!.id;
    modalRef.componentInstance.hasLoginAccount = !!this.driver?.hasLoginAccount;

    modalRef.result.then((result) => {
      if (result === 'generated' && this.driver) {
        this.driver.hasLoginAccount = true;
      }
    }, () => undefined);
  }

  private validateRenewalDates(): void {
    if (this.driver?.dateOfBirth == '0001-01-01') {
      this.driver.dateOfBirth = undefined;
    }

    if (this.driver?.driverLicenseRenewal == '0001-01-01' || this.driver?.driverLicenseRenewal == '1900-01-01') {
      this.driver.driverLicenseRenewal = null;
    }

    if (this.driver?.professionalDriversPermitRenewalDate == '0001-01-01' || this.driver?.professionalDriversPermitRenewalDate == '1900-01-01') {
      this.driver.professionalDriversPermitRenewalDate = null;
    }

    if (this.driver?.driverLicenseRenewal) {
      const renewalDate = parseISO(this.driver.driverLicenseRenewal);
      this.licenseHasExpired = endOfDay(new Date()) >= endOfDay(renewalDate);
    }
  }

  private setDriverNotifications(): void {
    if (this.licenseHasExpired) {
      this.driverNotificationMessage = this.transloco.translate('driverInformation.status.documentExpired');
    }

    if (this.driverTripsOverview?.licenseExpiringSoon && !this.licenseHasExpired) {
      this.driverNotificationMessage = this.transloco.translate('driverInformation.status.documentExpiringSoon');
    }
  }

  private clearSelectedPhoto(): void {
    this.photoFile = null;
    this.selectedPhotoFile = undefined;

    this.photoUrl = this.driver?.profilePictureUrl || this.defaultProfile;

    const fileInput = document.getElementById('photoInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  private getFormData(driver: DriverInformationDetailed): FormData {
    const formData = new FormData();

    if (this.photoFile) {
      this.driver!.profilePicture = this.selectedPhotoFile;
      formData.append("ProfilePicture", this.selectedPhotoFile!)
    }

    formData.append("DriverIdNumber", driver.driverIdNumber ?? '');
    formData.append("DriverLicenseRenewal", driver.driverLicenseRenewal!);
    formData.append("ProfessionalDriversPermitRenewalDate", driver.professionalDriversPermitRenewalDate!);
    formData.append("DateOfBirth", driver.dateOfBirth!);
    formData.append("Comments", driver.comments ?? '');
    formData.append("PassportNumber", driver.passportNumber ?? '');
    formData.append("PhoneNumber", driver.phoneNumber!);
    formData.append("AlternativePhoneNumber", driver.alternativePhoneNumber ?? '');
    formData.append("Email", driver.email!);
    formData.append("Address", driver.address!);
    formData.append("AccountId", driver.accountId.toString());
    formData.append("AccountName", driver.accountName);
    formData.append("FirstName", driver.firstName);
    formData.append("LastName", driver.lastName);
    formData.append('HasProfilePicture', `${!!this.photoFile || this.driver!.hasProfilePicture}`);
    formData.append("Id", driver.id.toString());

    return formData;
  }
}